<script setup>
import { useStore } from "vuex";
import { computed, defineProps } from "vue";
import { getLocationDetails } from "../../../helpers/locationHelper";
import { isLocationProductAvailableForFinishing } from "../../../helpers/projectHelper";
import CustomizationLocation from "./customization-location.vue";
import { PRODUCT_FINISHING_PRINTING } from "../../../services/constants";

const store = useStore();

const props = defineProps({
    toggle: {
        type: Boolean,
        required: true,
    },
    selected: {
        type: Boolean,
        required: true,
    },
    locationId: {
        type: String,
        required: true,
    },
    selectedLocations: {
        type: Array,
        required: true,
    },
});

const article = computed(() => store.getters["project/currentArticle"]);
const product = computed(() => store.getters["project/productConfiguration"]);

const isDisabledLocationByFinishing = computed(() => {
    if (!article.value.finishing) {
        return false;
    }
    return !isLocationProductAvailableForFinishing(product.value.catalogId, props.locationId, article.value.finishing);
});

const isDisabledLocation = computed(() => {
    const isLocationDisabledByAnOther = props.selectedLocations.some((loc) => {
        if (article.value.finishing !== null) {
            return getLocationDetails(loc).locationsToDisable[article.value.finishing.toLowerCase()].includes(
                props.locationId,
            );
        }
        const locationsForEmbroidery = getLocationDetails(loc).locationsToDisable.embroidery;
        const locationsForPrinting = getLocationDetails(loc).locationsToDisable.printing;
        const intersections = locationsForEmbroidery.filter((item) => locationsForPrinting.includes(item));
        return intersections.includes(props.locationId);
    });

    return isDisabledLocationByFinishing.value || isLocationDisabledByAnOther;
});

function getFinishingLabel(finishing) {
    return finishing === PRODUCT_FINISHING_PRINTING ? "transfert" : "broderie";
}
</script>

<template>
    <div :class="[toggle && 'toggle', selected && 'border-theme-blue bg-theme-blue-100', 'location py-4']">
        <div
            v-if="isDisabledLocation"
            class="flex cursor-not-allowed flex-row flex-wrap items-center justify-between px-6 font-bold"
        >
            <span class="flex items-center opacity-60">
                <input
                    type="checkbox"
                    class="form-tick mr-4 h-5 w-5 cursor-pointer appearance-none rounded border-2 border-gray-400 bg-white checked:border-theme-blue checked:bg-theme-blue"
                    disabled
                />
                {{ getLocationDetails(locationId).name }}
            </span>
            <span class="opacity-60">
                <i class="icon-arrow-up4" />
            </span>
            <span v-if="isDisabledLocationByFinishing" class="w-full text-red-600">
                Cet emplacement n'est pas disponible avec cette finition ({{ getFinishingLabel(article.finishing) }})
            </span>
        </div>
        <div
            v-else
            class="flex flex-row items-center justify-between px-6 font-bold"
            @click.stop="$emit('toggle', locationId)"
        >
            <span class="flex items-center">
                <input
                    :id="`location-${locationId}`"
                    type="checkbox"
                    class="form-tick mr-4 h-5 w-5 cursor-pointer appearance-none rounded border-2 border-gray-400 bg-white checked:border-theme-blue checked:bg-theme-blue"
                    :checked="selectedLocations.includes(locationId)"
                    @click.stop="$emit('select-location', locationId)"
                />
                {{ getLocationDetails(locationId).name }}
            </span>
            <span class="cursor-pointer">
                <i :class="[toggle && 'hidden', 'icon-arrow-up4']" />
                <i :class="[!toggle && 'hidden', 'icon-arrow-down5']" />
            </span>
        </div>
        <div class="content ml-10 flex flex-col">
            <div class="mb-3 flex flex-col">
                Zone {{ locationId }} - Dimensions : {{ getLocationDetails(locationId).dimension.width }}cm x
                {{ getLocationDetails(locationId).dimension.height }}cm
                <span class="text-xs text-red-700">
                    Les dimensions de la zone ne garantissent pas les dimensions du marquage
                </span>
            </div>
            <CustomizationLocation
                :location-id="locationId"
                @customization-update="$emit('update-customization', $event)"
            />
        </div>
    </div>
</template>
